import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';

// --- IMPORT BACKGROUND IMAGE ---
import MasterBKG from '../../Common/Images/MasterBKG.png';

interface DicOrder {
    DicFilename: string;
    ShortDesc: string;
    DicFileOrder?: number | null;
}

const EL_WriteDICToDB: React.FC = () => {

    const API_BASE =
        process.env.REACT_APP_DOCUMENT_LINK;

    // --- State: Order List ---
    const [dicList, setDicList] = useState<DicOrder[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    // --- State: Write Form ---
    const [physicalFiles, setPhysicalFiles] = useState<string[]>([]);
    const [selectedFile, setSelectedFile] = useState('-- Select --');
    const [selectedSubject, setSelectedSubject] = useState('-- Select --');

    // --- State: Modal ---
    const [showModal, setShowModal] = useState(false);
    const [missingLessons, setMissingLessons] = useState<string[]>([]);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.body.style.backgroundColor = '#214461';
        document.body.style.margin = '0px';
        fetchInitData();
    }, []);

    const fetchInitData = async () => {
        setLoading(true);
        try {
            // both calls run in parallel instead of one after the other
            const [orderRes, filesRes] = await Promise.all([
                fetch(`${API_BASE}api/dic_db/order_list`),
                fetch(`${API_BASE}api/dic_db/physical_files`),
            ]);
            const [orderJson, filesJson] = await Promise.all([orderRes.json(), filesRes.json()]);
            if (orderJson.dics) setDicList(orderJson.dics);
            if (filesJson.files) setPhysicalFiles(filesJson.files);
        } catch (err) {
            console.error(err);
            alert("Error loading initial data.");
        } finally {
            setLoading(false);
        }
    };

    // After an import only the order list changes, so skip the slow blob listing
    const refreshOrderList = async () => {
        try {
            const res = await fetch(`${API_BASE}api/dic_db/order_list`);
            const json = await res.json();
            if (json.dics) setDicList(json.dics);
        } catch (err) {
            console.error(err);
        }
    };

    // --- Reordering Logic ---
    const moveUp = () => {
        if (selectedIndex === null || selectedIndex <= 0) return;
        const newList = [...dicList];
        const temp = newList[selectedIndex - 1];
        newList[selectedIndex - 1] = newList[selectedIndex];
        newList[selectedIndex] = temp;
        setDicList(newList);
        setSelectedIndex(selectedIndex - 1);
    };

    const moveDown = () => {
        if (selectedIndex === null || selectedIndex >= dicList.length - 1) return;
        const newList = [...dicList];
        const temp = newList[selectedIndex + 1];
        newList[selectedIndex + 1] = newList[selectedIndex];
        newList[selectedIndex] = temp;
        setDicList(newList);
        setSelectedIndex(selectedIndex + 1);
    };

    const handleSaveOrder = async () => {
        setLoading(true);
        try {
            const orderedNames = dicList.map(d => d.DicFilename);
            const res = await fetch(`${API_BASE}api/dic_db/save_order`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ordered_dics: orderedNames })
            });
            const json = await res.json();
            if (json.success) {
                alert("Order saved successfully.");
                refreshOrderList();
            }
        } catch (err) {
            alert("Failed to save order.");
        } finally {
            setLoading(false);
        }
    };

    // --- Write to DB Logic ---
    const handleAddInit = async () => {
        if (selectedFile === '-- Select --') return alert("Select DIC File.");
        if (selectedSubject === '-- Select --') return alert("Select a subject to which the selected DIC File belongs.");

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}api/dic_db/check_impact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dic_name: selectedFile, subject: selectedSubject })
            });
            const json = await res.json();
            if (json.error) throw new Error(json.error);

            const existing = dicList.find(
                d => d.DicFilename.toLowerCase() === selectedFile.toLowerCase()
            );
            if (existing && (existing.DicFileOrder === null || existing.DicFileOrder === undefined)) {
                setAlertInfo({
                    show: true,
                    message: `DIC File Order is missing for ${selectedFile}. Click Save in the order list to set it, then try Add again.`
                });
                return;
            }

            setMissingLessons(json.missing_lessons || []);
            setShowModal(true);
        } catch (err) {
            alert("Error analyzing DIC file impact.");
        } finally {
            setLoading(false);
        }
    };

    const confirmWriteToDb = async () => {
        setShowModal(false);
        setLoading(true);
        try {
            const adminLogin = sessionStorage.getItem("SES_ADMIN_LOGIN") || "Admin";
            const res = await fetch(`${API_BASE}api/dic_db/write_to_db`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    dic_name: selectedFile,
                    subject: selectedSubject,
                    admin_login: adminLogin,
                    missing_lessons: missingLessons   // NEW: the lessons the admin just confirmed
                })
            });
            const json = await res.json();
            if (json.error) alert(json.error);
            else {
                alert(json.message);
                setMissingLessons([]);
                refreshOrderList();      // was: fetchInitData()
            }
        } catch (err) {
            alert("Error writing DIC to Database.");
        } finally {
            setLoading(false);
        }
    };
    const handleBack = () => {
        window.location.href = "/WebAdmin/Dic/EL_ManageDIC";
    };

    // --- Styles matching original layout exactly ---
    const styles = {
        mainWrapper: { display: 'flex', justifyContent: 'center', fontFamily: 'Arial, sans-serif', fontSize: '10pt', color: 'black' },
        bgBox: {
            backgroundImage: `url(${MasterBKG})`,
            width: '800px', height: '590px', overflow: 'hidden',
            backgroundRepeat: 'no-repeat', display: 'flex', flexDirection: 'column' as const, alignItems: 'center'
        },
        headerTable: { width: '88%', borderSpacing: 0 },
        headerTd: { color: 'white', fontWeight: 'bold', backgroundColor: 'blue', borderRight: '1px solid black', borderLeft: '1px solid black', padding: '5px' },

        listWrapper: { height: '280px', overflowY: 'scroll' as const, overflowX: 'hidden' as const, border: '1px solid #003300', backgroundColor: 'white' },
        listRow: { padding: '5px', cursor: 'pointer', display: 'flex' },

        rightPanel: { border: '1px solid #003300', backgroundColor: 'AliceBlue', padding: '10px' },

        // Modal Prompt styles mapped from the head script
        modalWrapper: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: '120px', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 },
        promptBox: { background: 'white', borderRadius: '6px', boxShadow: '0px 0px 10px #999', fontFamily: 'Arial, sans-serif', minWidth: '420px' },
        promptHeader: { background: '#cccccc', padding: '10px', fontSize: '16px', textAlign: 'center' as const, fontWeight: 'bold' },
        promptBody: { padding: '20px', fontSize: '14px', maxHeight: '300px', overflowY: 'auto' as const },
        promptFooter: { padding: '15px', textAlign: 'right' as const, borderTop: '1px solid #eee' },
        promptBtn: { padding: '6px 18px', marginLeft: '10px', cursor: 'pointer' },

        btn: { backgroundColor: '#119ECC', color: 'white', fontWeight: 'bold', border: 'solid 2px orange', padding: '4px 10px', cursor: 'pointer', fontSize: '10pt' }
    };
    const linkLabel = selectedSubject === 'English' ? 'EC' : 'MD';

    return (
        <div style={styles.mainWrapper}>
            {loading && <Loading />}
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}

            <div style={styles.bgBox}>
                <div style={{ height: '100px' }}></div>

                <table style={styles.headerTable} cellPadding="5">
                    <tbody>
                        <tr>
                            <td colSpan={3} style={{ color: 'blue', fontWeight: 'bold', textAlign: 'center', textDecoration: 'underline', height: '25px', verticalAlign: 'top' }}>
                                DIC files
                            </td>
                        </tr>
                        <tr>
                            <td style={{ ...styles.headerTd, width: '60%' }}>Setting DIC File Order</td>
                            <td style={{ width: '1%' }}></td>
                            <td style={{ ...styles.headerTd, width: '39%' }}>Adding Dic file to DB</td>
                        </tr>
                        <tr>
                            {/* Left Column: Sortable List */}
                            <td style={{ verticalAlign: 'top', padding: 0 }}>
                                <div style={styles.listWrapper}>
                                    {dicList.map((dic, idx) => (
                                        <div
                                            key={dic.DicFilename}
                                            onClick={() => setSelectedIndex(idx)}
                                            style={{
                                                ...styles.listRow,
                                                backgroundColor: selectedIndex === idx ? 'orange' : (idx % 2 === 0 ? '#F4FAFF' : '#EAEEEE'),
                                                color: selectedIndex === idx ? 'white' : 'black'
                                            }}
                                        >
                                            <div style={{ width: '40%' }}>{dic.DicFilename}</div>
                                            <div style={{ width: '60%' }}>{dic.ShortDesc}</div>
                                        </div>
                                    ))}
                                </div>
                            </td>
                            <td></td>
                            {/* Right Column: Write DB Form */}
                            <td style={{ ...styles.rightPanel, verticalAlign: 'top' }}>
                                <table style={{ width: '100%' }}>
                                    <tbody>
                                        <tr><td colSpan={3} style={{ height: '10px' }}></td></tr>
                                        <tr>
                                            <td style={{ width: '33%', textAlign: 'right' }}>DIC File:</td>
                                            <td style={{ width: '2%' }}></td>
                                            <td style={{ width: '75%', textAlign: 'left' }}>
                                                <select style={{ width: '150px' }} value={selectedFile} onChange={e => setSelectedFile(e.target.value)}>
                                                    <option value="-- Select --">-- Select --</option>
                                                    {physicalFiles.map(f => <option key={f} value={f}>{f}</option>)}
                                                </select>
                                            </td>
                                        </tr>
                                        <tr><td colSpan={3} style={{ height: '10px' }}></td></tr>
                                        <tr>
                                            <td style={{ textAlign: 'right' }}>Subject:</td>
                                            <td></td>
                                            <td style={{ textAlign: 'left' }}>
                                                <select style={{ width: '150px' }} value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                                                    <option value="-- Select --">-- Select --</option>
                                                    <option value="Mathematics">Mathematics</option>
                                                    <option value="English">English</option>
                                                    <option value="Science">Science</option>
                                                </select>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            {/* Bottom Actions Left */}
                            <td style={{ border: '1px solid #003300', borderTop: 'none', padding: '5px' }}>
                                <button style={styles.btn} onClick={moveUp}>Move Up</button>&nbsp;&nbsp;
                                <button style={styles.btn} onClick={moveDown}>Move Down</button>&nbsp;&nbsp;
                                <button style={styles.btn} onClick={handleSaveOrder}>Save</button>
                            </td>
                            <td></td>
                            {/* Bottom Actions Right */}
                            <td style={{ border: '1px solid #003300', borderTop: 'none', textAlign: 'center', padding: '5px' }}>
                                <button style={styles.btn} onClick={handleAddInit}>Add</button>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={3} style={{ textAlign: 'center', paddingTop: '15px' }}>
                                <button style={styles.btn} onClick={handleBack}>Back</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Custom Modal Prompt (Replaces div_mdlesson) */}
            {showModal && (
                <div style={styles.modalWrapper}>
                    <div style={styles.promptBox}>
                        <div style={styles.promptHeader}>Confirmation</div>
                        <div style={styles.promptBody}>
                            {missingLessons.length > 0 ? (
                                <div>
                                    <div style={{ marginBottom: '6px', fontWeight: 'bold', padding: '5px' }}>
                                        The following {linkLabel}-linked lessons are not available after modifying this DIC
                                        <br />Are you sure you want to modify the DIC file?
                                    </div>
                                    <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                        {missingLessons.map(lsn => (
                                            <li key={lsn} style={{ padding: '5px' }}>{lsn}</li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
                                    No {linkLabel}-linked lessons are affected after modifying the DIC.
                                    <br />Are you sure you want to modify the DIC file?
                                </div>
                            )}
                        </div>
                        <div style={styles.promptFooter}>
                            <button style={styles.promptBtn} onClick={confirmWriteToDb}>OK</button>
                            <button style={styles.promptBtn} onClick={() => setShowModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EL_WriteDICToDB;